import { StaticModal, createModal } from '@nexelem/modals';
import { Button, Banner } from '@nexelem/components';
import { useCustomToast } from '@nexelem/snackbar';
import { faFlaskGear } from '@fortawesome/pro-light-svg-icons';
import { useForm } from 'react-hook-form';
import { FormSelectInput } from '@nexelem/form-components';
import { useCustomTranslation } from '../../../../../hooks/useCustomTranslation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TaskView } from '@nexelem/graphne-api';
import { useGetGraphneTaskTechnologies } from '../hooks';

interface ChangeTechnologyModalProps {
  task: TaskView;
  currentTechnology?: string;
  onChange: (technology: string) => void;
}

interface UpdateTechnologyFormData {
  selectedTechnology: { value: string; label: string };
}

export const ChangeTechnologyModal = ({ task, currentTechnology, onChange }: ChangeTechnologyModalProps) => {
  const { tWrapper } = useCustomTranslation('products.simulationPlan');
  const displayToast = useCustomToast();
  const { data: technologies = [] } = useGetGraphneTaskTechnologies(task.id);
  const initTechnology = currentTechnology || task.qapsSimulationData?.recipeVersion;

  const defaultSelectedTechnology = {
    value: initTechnology || '',
    label: initTechnology || '',
  };

  const form = useForm<UpdateTechnologyFormData>({
    defaultValues: {
      selectedTechnology: defaultSelectedTechnology,
    },
  });

  const technologyOptions =
    technologies.length > 0
      ? technologies.map((tech) => {
          return {
            value: tech,
            label: tech,
          };
        })
      : [defaultSelectedTechnology];

  const handleSubmit = form.handleSubmit((data) => {
    onChange(data.selectedTechnology.value);

    displayToast({
      toastType: 'success',
      message: tWrapper('changeTechnologySuccessToast'),
    });

    closeChangeTechnologyModal();
  });

  return (
    <StaticModal component="form" maxWidth="lg" onClose={closeChangeTechnologyModal} onSubmit={handleSubmit}>
      <StaticModal.Header withCloseButton>{tWrapper('changeTechnologyModalTitle')}</StaticModal.Header>
      <StaticModal.Content className="flex flex-col gap-6">
        <div className="flex items-end justify-between rounded-xs border border-border-container bg-surface-container-lighter px-4 py-3">
          <div className="flex flex-col gap-1 text-s11-sb">
            <p className="inline-flex gap-2 uppercase">
              <span>{tWrapper('changeTechnologyProductNumber')}</span>
              <span>{task.productDetails.code}</span>
            </p>
            <span className="text-s16-sb">{task.productDetails.name}</span>
          </div>
          <p className="inline-flex items-center gap-2">
            <span className="text-s16-sb">{task.progress.totalAmount.amount}</span>
            <span className="text-s16">{task.progress.totalAmount.unit.name}</span>
          </p>
        </div>
        <p className="text-s18">{tWrapper('changeTechnologySelectText')}</p>
        <FormSelectInput
          required
          control={form.control}
          getOptionLabel={(option) => option.label}
          label={tWrapper('changeTechnologyLabel')}
          maxWidth="full"
          name="selectedTechnology"
          options={technologyOptions}
          placeholder={tWrapper('changeTechnologyPlaceholder')}
          StartIcon={() => <FontAwesomeIcon className="mr-2" icon={faFlaskGear} />}
        />
        <Banner emphasis="weak" message={tWrapper('changeTechnologyWarning')} type="warning" />
      </StaticModal.Content>
      <StaticModal.Footer>
        <Button variant="outlined" onClick={closeChangeTechnologyModal}>
          {tWrapper('simulationCancel')}
        </Button>
        <Button type="submit" variant="contained">
          {tWrapper('changeTechnologyApply')}
        </Button>
      </StaticModal.Footer>
    </StaticModal>
  );
};

export const { openChangeTechnologyModal, closeChangeTechnologyModal } = createModal(ChangeTechnologyModal, {
  name: 'ChangeTechnologyModal',
});
